import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { FilePlus2, FileText, Users, DollarSign, ArrowRight, Inbox } from "lucide-react";
import { RenewalBanner } from "@/components/PlanChip";

const statusColor = {
  Draft: "bg-slate-100 text-slate-700",
  Sent: "bg-blue-100 text-blue-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-rose-100 text-rose-700",
  Paid: "bg-orange-100 text-orange-700",
};

export default function DashboardPage() {
  const [stats, setStats] = useState({ quotes_this_month: 0, total_quotes: 0, total_value: 0, saved_clients: 0 });
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/quotes/stats"), api.get("/quotes")])
      .then(([s, q]) => {
        setStats(s.data);
        setQuotes(q.data.slice(0, 8));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto" data-testid="dashboard-page">
      <RenewalBanner />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Messy notes in. Professional quote out.</p>
        </div>
        <Link to="/app/new" data-testid="dashboard-new-quote-button">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-5">
            <FilePlus2 className="h-4 w-4 mr-2" /> New Quote
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileText} label="This month" value={stats.quotes_this_month} testid="stat-month" />
        <StatCard icon={FilePlus2} label="Total quotes" value={stats.total_quotes} testid="stat-total" />
        <StatCard icon={DollarSign} label="Total value" value={`$${Number(stats.total_value).toLocaleString()}`} testid="stat-value" />
        <StatCard icon={Users} label="Saved clients" value={stats.saved_clients} testid="stat-clients" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-semibold text-slate-900">Recent quotes</h2>
        <Link to="/app/quotes" className="text-sm font-medium text-orange-600 hover:text-orange-700" data-testid="view-all-quotes">
          View all <ArrowRight className="inline h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400 border-slate-200">Loading…</Card>
      ) : quotes.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-slate-300 bg-white" data-testid="empty-quotes-card">
          <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <h3 className="font-display text-lg font-semibold text-slate-800">Create your first professional contractor quote.</h3>
          <p className="text-slate-500 text-sm mt-1 mb-5">Paste rough notes, add a price, and let ScopeWriter handle the rest.</p>
          <Link to="/app/new">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              <FilePlus2 className="h-4 w-4 mr-2" /> New Quote
            </Button>
          </Link>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="divide-y divide-slate-100">
            {quotes.map((q) => (
              <Link
                key={q.id} to={`/app/quotes/${q.id}`}
                data-testid={`quote-row-${q.id}`}
                className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-900 truncate">{q.generated?.project_title || "Untitled quote"}</div>
                    <Badge className={`${statusColor[q.status] || statusColor.Draft} hover:opacity-90`}>{q.status}</Badge>
                  </div>
                  <div className="text-sm text-slate-500 mt-1 truncate">
                    {q.customer?.name || "—"} · {q.customer?.address || "No address"} · {new Date(q.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="font-display font-semibold text-slate-900">${Number(q.total_price || 0).toLocaleString()}</div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-5 border-slate-200 shadow-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="h-8 w-8 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-slate-900">{value}</div>
    </Card>
  );
}
